<script lang="ts">
  // Plays — area central da mesa com as cartas que os jogadores
  // colocaram. Em 1v1: 2 fileiras (oponente em cima, eu embaixo),
  // cada uma ate 3 cartas (1 por vaza).
  //
  // Cartas vem dos campos first_play/second_play/third_play do
  // PlayerSnapshot.
  import type { Card as CardType, PlayerSnapshot } from '../../stores/match';
  import Card from './Card.svelte';
  import { receiveCard } from '../../lib/cardTransition';

  // Carta do oponente cai de cima girando — antes era classe CSS estatica
  // `card-fly-from-top` no template, mas em mobile/aba background o
  // browser suprime animacoes CSS quando o elemento entra no DOM offscreen,
  // ai a carta ficava invisivel ate o jogador interagir (forcando repaint).
  // Felipe 2026-05-27: "primeiro turno, n da p ver ele atacar a carta, so
  // aparece quando eu jogo". Svelte transition garante execucao no mount
  // independente do estado de visibilidade da aba.
  function flyFromTop(_node: Element) {
    return {
      duration: 520,
      easing: (t: number) => 1 - Math.pow(1 - t, 3),
      css: (t: number) => {
        const u = 1 - t;
        const y = -220 * u;
        const ry = 540 * u;
        // overshoot: pico em scale 1.05 perto do fim, igual keyframe original
        const s = t < 0.85 ? 0.7 + (1.05 - 0.7) * (t / 0.85) : 1.05 - 0.05 * ((t - 0.85) / 0.15);
        return `transform: translateY(${y}px) rotateY(${ry}deg) scale(${s}); opacity: ${Math.min(1, t * 1.5)};`;
      },
    };
  }

  type Props = {
    me: PlayerSnapshot;
    opp: PlayerSnapshot;
    vira?: CardType | null;
  };
  let { me, opp, vira }: Props = $props();

  // Trick number atual = quantos slots ja tem carta jogada (do maior).
  let oppPlays = $derived(
    [opp.first_play, opp.second_play, opp.third_play].filter(Boolean) as CardType[],
  );
  let mePlays = $derived(
    [me.first_play, me.second_play, me.third_play].filter(Boolean) as CardType[],
  );

  // cardKey gera a mesma chave usada pelo Hand pro crossfade. Carta
  // virada (flipped) usa chave generica + index pra ainda animar com
  // fade (mas sem origem rastreavel). Carta normal: value+suit.
  function cardKey(card: CardType, i: number): string {
    if (!card.value || !card.suit) return `back-${i}`;
    return `${card.value}${card.suit}`;
  }
</script>

<div class="plays">
  <div class="row opp-row">
    {#each oppPlays as card, i (i)}
      <!-- Carta do opp vem de cima girando via Svelte transition (mount-safe). -->
      <div class="play-slot" in:flyFromTop>
        <Card {card} {vira} size="md" />
      </div>
    {/each}
  </div>

  <div class="separator">
    <span class="line"></span>
    <span class="vira-label">mesa</span>
    <span class="line"></span>
  </div>

  <div class="row me-row">
    {#each mePlays as card, i (i)}
      <!-- Minha carta tem crossfade pair com a hand — desliza do slot
           da mao com spin. cardKey bate com sendCard de Hand.svelte. -->
      <div class="play-slot" in:receiveCard={{ key: cardKey(card, i) }}>
        <Card {card} {vira} size="md" />
      </div>
    {/each}
  </div>
</div>

<style>
  .plays {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(4px, 1.5vh, 10px);
    padding: clamp(8px, 2vh, 14px) 10px;
    min-height: 0;
    height: 100%;
    justify-content: space-around;
    width: 100%;
  }
  .row {
    display: flex;
    justify-content: center;
    gap: 6px;
    min-height: 0;
  }
  .row.opp-row { transform: rotate(180deg); }
  .row.opp-row :global(.card) {
    /* mantem as cartas legiveis mesmo com a row rotacionada */
    transform: rotate(180deg);
  }
  .separator {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 280px;
  }
  .separator .line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border), transparent);
  }
  .vira-label {
    font-size: 10px;
    color: var(--text-dim);
    letter-spacing: 2px;
    text-transform: uppercase;
  }
</style>
